import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, Loader2, PackageSearch } from "lucide-react";
import client from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { TechnicianBar } from "@/components/TechnicianBar";
import { useStore } from "@/context/StoreContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const SORTS = [["relevance", "Relevance"], ["newest", "Newest"], ["az", "Name A–Z"], ["sku", "SKU"], ["availability", "Availability"]];

const Catalogue = () => {
  const { slug, brand } = useParams();
  const [sp, setSp] = useSearchParams();
  const { techMode } = useStore();

  const search = sp.get("search") || "";
  const [sort, setSort] = useState(sp.get("sort") || "relevance");
  const [availability, setAvailability] = useState(sp.get("availability") || "");
  const [activeBrand, setActiveBrand] = useState(brand || "");
  const [page, setPage] = useState(1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    client.get("/categories").then((r) => setCats(r.data.flat));
    client.get("/brands").then((r) => setBrands(r.data.items));
  }, []);

  useEffect(() => { setActiveBrand(brand || ""); setPage(1); }, [brand, slug]);
  useEffect(() => { setPage(1); }, [search, sort, availability, activeBrand]);

  useEffect(() => {
    setLoading(true);
    const params = { sort, page, limit: 24 };
    if (search) params.search = search;
    if (slug) params.category = slug;
    if (availability) params.availability = availability;
    if (activeBrand) params.brand = activeBrand;
    client.get("/products", { params }).then((r) => { setData(r.data); setLoading(false); });
  }, [search, slug, sort, availability, activeBrand, page]);

  const title = useMemo(() => {
    if (search) return `Results for “${search}”`;
    if (brand) return brand;
    if (slug) { const c = cats.find((x) => x.slug === slug); return c ? c.name : "Catalogue"; }
    return "All Products";
  }, [search, brand, slug, cats]);

  const FilterPanel = (
    <div className="space-y-6">
      <div>
        <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-3">Availability</div>
        <div className="space-y-1">
          {[["", "All"], ["in_stock", "In stock"], ["out_of_stock", "Out of stock"]].map(([v, l]) => (
            <button key={v} data-testid={`filter-avail-${v || "all"}`} onClick={() => setAvailability(v)} className={`block w-full text-left text-sm px-2 py-1.5 rounded ${availability === v ? "bg-orange/10 text-orange font-medium" : "text-steel hover:bg-surface"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-3">Brand</div>
        <div className="max-h-64 overflow-y-auto thin-scroll space-y-1 pr-1">
          <button onClick={() => setActiveBrand("")} className={`block w-full text-left text-sm px-2 py-1.5 rounded ${!activeBrand ? "bg-orange/10 text-orange font-medium" : "text-steel hover:bg-surface"}`}>All brands</button>
          {brands.map((b) => (
            <button key={b.slug} data-testid={`filter-brand-${b.slug}`} onClick={() => setActiveBrand(b.name)} className={`flex w-full items-center justify-between text-left text-sm px-2 py-1.5 rounded ${activeBrand === b.name ? "bg-orange/10 text-orange font-medium" : "text-steel hover:bg-surface"}`}>
              <span className="truncate">{b.name}</span><span className="mono text-xs text-steel-light">{b.count}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="App min-h-screen flex flex-col bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full py-6 flex-1">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div>
            <h1 data-testid="catalogue-title" className="text-2xl font-bold tracking-tight text-graphite">{title}</h1>
            <p className="text-sm text-steel-light mt-0.5">{data ? `${data.total} parts` : "…"}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="lg:hidden" data-testid="toggle-filters" onClick={() => setShowFilters((s) => !s)}>
              <SlidersHorizontal className="w-4 h-4 mr-1.5" /> Filters
            </Button>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="w-[150px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-6">
          <aside className="hidden lg:block w-56 shrink-0">{FilterPanel}</aside>

          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="grid place-items-center py-32 text-steel-light"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : data?.items?.length ? (
              <>
                <div className={`grid gap-4 ${techMode ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"}`}>
                  {data.items.map((p) => <ProductCard key={p.sku} product={p} />)}
                </div>
                {data.pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <Button variant="outline" size="sm" disabled={page <= 1} data-testid="page-prev" onClick={() => setPage((p) => p - 1)}>Previous</Button>
                    <span className="mono text-sm text-steel px-2">{page} / {data.pages}</span>
                    <Button variant="outline" size="sm" disabled={page >= data.pages} data-testid="page-next" onClick={() => setPage((p) => p + 1)}>Next</Button>
                  </div>
                )}
              </>
            ) : (
              <div className="grid place-items-center py-32 text-center">
                <PackageSearch className="w-10 h-10 text-steel-light mb-3" />
                <p className="text-steel font-medium">No parts found</p>
                <p className="text-sm text-steel-light">Try a different search or clear filters.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85%] bg-white p-5 overflow-y-auto thin-scroll">
            <div className="flex items-center justify-between mb-4"><span className="font-semibold">Filters</span><button onClick={() => setShowFilters(false)}><X className="w-5 h-5" /></button></div>
            {FilterPanel}
          </div>
        </div>
      )}

      <Footer />
      <TechnicianBar />
    </div>
  );
};

export default Catalogue;
